import React, { useState, useEffect, useMemo } from 'react';
import { X, CheckCircle, Building2, Mail, Phone, FileText, ArrowRight, AlertCircle, Coins, Clock, Zap, MessageSquare, Ruler, Layers, Loader2, Palette, Sparkles, PoundSterling, CreditCard } from 'lucide-react';
import { CREDIT_PRICE_GBP } from '../constants';
import {
  calculateDirectCardPrice,
  creditsForPence,
  formatGBP,
  SKETCHPLAN_COMMERCIAL_RULE_SHORT,
  SKETCHPLAN_PRICING_COPY,
  SKETCHPLAN_QUOTE_SQFT,
} from '@rehub/pricing';
import type { BrandingProfile } from '../types';

/** Properties over this size are quoted individually (matches the pricing engine). */
const CUSTOM_QUOTE_SQFT = SKETCHPLAN_QUOTE_SQFT;
/** Credits charged per £ of order value, derived from the credit purchase rate. */
const poundsToCredits = (pence: number): number => creditsForPence(pence, CREDIT_PRICE_GBP);

/** Legacy type for backwards compatibility; analysis is no longer used (manual sq ft only). */
export interface SketchAnalysis {
  estimatedSqFt: number;
  roomCount: number;
  complexity: string;
  hasMultipleFloors: boolean;
  features: string[];
  confidence: string;
  creditCost: number;
}

interface ProOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  sketchUrls: string[];
  sketchStorageUrls?: string[];
  isUploadingSketch?: boolean;
  uploadProgress?: number;
  userEmail?: string;
  userCredits: number;
  /** Unused — public volume pricing has been removed. Kept so callers do not break. */
  monthlyVolume?: number;
  /** 'monthly_invoice' clients are billed on a statement and don't use credits. */
  billingMode?: 'prepaid' | 'monthly_invoice';
  /** Optional negotiated flat per-plan rate (pence) for this client; null = published table. */
  customPlanRatePence?: number | null;
  /** No longer used; customers enter sq ft and floors manually */
  analysis?: SketchAnalysis | null;
  isAnalyzing?: boolean;
  onSubmit: (orderData: ProOrderData) => Promise<boolean>;
  /** Open dashboard to buy credits; pass credits needed to pre-fill "Buy exact credits" */
  onTopUp: (creditsNeeded?: number) => void;
  /** Agency branding profile to include with the order */
  brandingProfile?: BrandingProfile;
  /** Open branding modal */
  onOpenBranding?: () => void;
}

export interface ProOrderData {
  propertyAddress: string;
  estimatedSqFt: number;
  propertyType: 'residential' | 'commercial';
  floors: number;
  notes: string;
  contactEmail: string;
  contactPhone: string;
  sketchUrls: string[];
  turnaround: 'standard' | 'express';
  creditCost: number;
  roomCount: number;
  complexity: string;
  /** Branding spec for the CAD team */
  branding?: BrandingProfile;
  /** How this order is settled. */
  paymentMethod: 'credits' | 'invoice' | 'card';
  /** For invoice orders: £ amount (pence) added to the monthly invoice. */
  invoiceAmountPence?: number | null;
  /** Effective per-plan price in pence (after uplifts) — used for card checkout. */
  amountPence: number | null;
}

const ProOrderModal: React.FC<ProOrderModalProps> = ({
  isOpen,
  onClose,
  sketchUrls,
  sketchStorageUrls = [],
  isUploadingSketch = false,
  uploadProgress = 0,
  userEmail = '',
  userCredits,
  monthlyVolume = 0,
  billingMode = 'prepaid',
  customPlanRatePence = null,
  analysis,
  isAnalyzing,
  onSubmit,
  onTopUp,
  brandingProfile,
  onOpenBranding,
}) => {
  const [selectedSketchIndex, setSelectedSketchIndex] = useState(0);
  const [formData, setFormData] = useState({
    propertyAddress: '',
    propertyType: 'residential' as 'residential' | 'commercial',
    notes: '',
    contactEmail: userEmail,
    contactPhone: '',
    turnaround: 'standard' as 'standard' | 'express',
    sqFt: '',
    floors: 1 as 1 | 2,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  // Prepaid clients can settle a plan with their credit balance or pay by card.
  const [payMethod, setPayMethod] = useState<'credits' | 'card'>('credits');

  // Sync contact email when userEmail prop changes
  useEffect(() => {
    if (userEmail) setFormData(prev => ({ ...prev, contactEmail: userEmail }));
  }, [userEmail]);

  // Trigger animation on open
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Manual sq ft from customer (required for pricing)
  const effectiveSqFt = useMemo(() => {
    const n = parseInt(formData.sqFt, 10);
    return Number.isNaN(n) ? 0 : Math.max(0, n);
  }, [formData.sqFt]);

  const isInvoiceClient = billingMode === 'monthly_invoice';

  const planPrice = useMemo(
    () => calculateDirectCardPrice({
      propertyType: formData.propertyType,
      sqFt: effectiveSqFt,
      turnaround: formData.turnaround,
      customPlanRatePence,
    }),
    [formData.propertyType, formData.turnaround, effectiveSqFt, customPlanRatePence]
  );

  const hasCustomRate = planPrice.usedCustomRate;
  const isExpress = formData.turnaround === 'express';
  const effectivePricePence = planPrice.totalPence;
  const commercialUpliftPence = planPrice.commercialUpliftPence;
  const listPricePence = planPrice.residentialPence;
  const expressUpliftPence = planPrice.expressPence;
  const requiresCustomQuote = planPrice.contactSales;

  // Credits remain the settlement unit for prepaid clients — convert £ at the
  // credit rate. Invoice clients are billed monthly and consume no credits.
  const creditCost = effectivePricePence != null ? poundsToCredits(effectivePricePence) : null;

  // Card payment is only an option for prepaid clients (invoice clients are
  // billed monthly and never pay at order time).
  const payingByCard = !isInvoiceClient && payMethod === 'card';
  const hasEnoughCredits =
    isInvoiceClient || (creditCost !== null && userCredits >= creditCost);
  // Whether the customer can place this order: invoice clients always can,
  // card payers always can (they pay now), credit payers need the balance.
  const canSettle = isInvoiceClient || payingByCard || hasEnoughCredits;
  const hasValidSqFt = effectiveSqFt >= 100 && effectiveSqFt <= 50000;

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasValidSqFt) {
      setError('Please enter a valid square footage (100–50,000 sq ft).');
      return;
    }

    if (sketchStorageUrls.length === 0) {
      setError('Please wait while we upload your sketches.');
      return;
    }

    if (requiresCustomQuote) {
      setError(`Properties over ${CUSTOM_QUOTE_SQFT.toLocaleString()} sq ft require a custom quote. Please contact us.`);
      return;
    }

    if (!isInvoiceClient && !payingByCard && (!hasEnoughCredits || creditCost === null)) {
      setError(`You need ${creditCost ?? 0} credits but only have ${userCredits}. Please purchase more credits.`);
      return;
    }

    setIsSubmitting(true);

    try {
      const orderData: ProOrderData = {
        propertyAddress: formData.propertyAddress,
        estimatedSqFt: effectiveSqFt,
        propertyType: formData.propertyType,
        floors: formData.floors,
        notes: formData.notes,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        sketchUrls: sketchStorageUrls,
        turnaround: formData.turnaround,
        creditCost,
        roomCount: 0,
        complexity: 'moderate',
        branding: brandingProfile,
        paymentMethod: isInvoiceClient ? 'invoice' : payingByCard ? 'card' : 'credits',
        invoiceAmountPence: isInvoiceClient ? effectivePricePence : null,
        amountPence: effectivePricePence,
      };

      const success = await onSubmit(orderData);
      if (success) {
        // Card orders redirect to Stripe Checkout, so don't show the in-app
        // success state — the confirmation happens after payment returns.
        if (!payingByCard) setIsSubmitted(true);
      } else {
        setError('Failed to submit order. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSubmitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
        <div 
          className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-slate-700/50 text-center"
          style={{
            animation: 'modalSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div 
            className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6"
            style={{ animation: 'scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both' }}
          >
            <CheckCircle size={40} className="text-green-400" />
          </div>
          <h3 
            className="text-2xl font-bold text-white mb-3"
            style={{ animation: 'fadeInUp 0.4s ease-out 0.3s both' }}
          >
            Order Received!
          </h3>
          <p 
            className="text-slate-400 mb-6 leading-relaxed"
            style={{ animation: 'fadeInUp 0.4s ease-out 0.4s both' }}
          >
            We're on it! Your professional floor plan will be delivered to your email within{' '}
            <span className="text-teal-400 font-medium">
              {formData.turnaround === 'express' ? '24 hours' : '48 hours'}
            </span>.
          </p>
          <div 
            className="bg-slate-800/50 rounded-xl p-4 mb-6 text-left"
            style={{ animation: 'fadeInUp 0.4s ease-out 0.5s both' }}
          >
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-1">
              <Mail size={14} />
              We'll send your floor plan to:
            </div>
            <div className="text-teal-400 font-medium">{formData.contactEmail}</div>
          </div>
          <button
            onClick={handleClose}
            className="w-full py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 transition-all shadow-lg shadow-teal-900/30 hover:shadow-teal-900/50 hover:scale-[1.02] active:scale-[0.98]"
            style={{ animation: 'fadeInUp 0.4s ease-out 0.6s both' }}
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Inline keyframes */}
      <style>{`
        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.97);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(20, 184, 166, 0.3); }
          50% { box-shadow: 0 0 40px rgba(20, 184, 166, 0.5); }
        }
      `}</style>

      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
          isVisible ? 'bg-black/70 backdrop-blur-md' : 'bg-black/0 backdrop-blur-none'
        }`}
        onClick={handleClose}
      >
        <div 
          className={`bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl w-full max-w-lg shadow-2xl border border-slate-700/50 flex flex-col max-h-[90vh] transition-all duration-300 ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
          }`}
          style={{
            transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Fixed */}
          <div className="flex items-center justify-between p-5 border-b border-slate-700/50 shrink-0">
            <div className="flex items-center gap-3" style={{ animation: 'fadeInUp 0.4s ease-out 0.1s both' }}>
              <img 
                src="/cropped-Sketchplan-Logo1-removebg-preview.webp" 
                alt="SketchPlan" 
                className="h-10 w-auto object-contain"
              />
              <div>
                <h2 className="text-xl font-bold text-white">
                  You Sketch, We Plan
                </h2>
                <p className="text-sm text-teal-300 font-medium mt-0.5">{SKETCHPLAN_PRICING_COPY.headline}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all hover:scale-110 active:scale-95"
            >
              <X size={20} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 overscroll-contain">
            {/* Sketch Preview with Analysis */}
            <div className="px-5 pt-4" style={{ animation: 'fadeInUp 0.4s ease-out 0.15s both' }}>
              <div className="flex gap-3">
                {/* Sketch Images */}
                <div className="w-2/5 space-y-2">
                  {/* Main preview */}
                  <div className="relative rounded-2xl overflow-hidden border border-slate-700/50 bg-slate-900 aspect-square">
                    <img 
                      src={sketchUrls[selectedSketchIndex] || ''} 
                      alt={`Sketch ${selectedSketchIndex + 1}`}
                      className="w-full h-full object-contain bg-white"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
                    {sketchUrls.length > 1 && (
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 px-2 py-1 rounded-full text-xs text-white">
                        {selectedSketchIndex + 1} / {sketchUrls.length}
                      </div>
                    )}
                  </div>
                  {/* Thumbnail strip for multiple sketches */}
                  {sketchUrls.length > 1 && (
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {sketchUrls.map((url, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedSketchIndex(index)}
                          className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                            selectedSketchIndex === index 
                              ? 'border-teal-500 ring-2 ring-teal-500/30' 
                              : 'border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <img 
                            src={url} 
                            alt={`Sketch ${index + 1}`}
                            className="w-full h-full object-cover bg-white"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Order details: manual sq ft and floors */}
                <div className="flex-1 bg-slate-900/50 rounded-2xl border border-slate-700/50 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-teal-400">
                    <Ruler size={14} />
                    <span className="text-xs font-semibold uppercase tracking-wide">Order details</span>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-400 mb-1.5 block">Square footage</label>
                    <input
                      type="number"
                      value={formData.sqFt}
                      onChange={(e) => setFormData({ ...formData, sqFt: e.target.value })}
                      placeholder="e.g. 1200"
                      min={100}
                      max={50000}
                      className="w-full px-3 py-2.5 text-lg font-semibold bg-slate-800 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {SKETCHPLAN_PRICING_COPY.fromSize} Over {CUSTOM_QUOTE_SQFT.toLocaleString()} sq ft = custom quote.
                    </p>
                    {formData.propertyType === 'commercial' && (
                      <p className="text-xs text-sky-400 mt-1">{SKETCHPLAN_PRICING_COPY.commercialSurcharge}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-400 mb-1.5 block">Floors</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, floors: 1 })}
                        className={`p-2.5 rounded-xl border text-sm font-medium transition-all ${
                          formData.floors === 1
                            ? 'border-teal-500/50 bg-teal-500/10 text-teal-400'
                            : 'border-slate-700/50 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        1 floor
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, floors: 2 })}
                        className={`p-2.5 rounded-xl border text-sm font-medium transition-all ${
                          formData.floors === 2
                            ? 'border-teal-500/50 bg-teal-500/10 text-teal-400'
                            : 'border-slate-700/50 bg-slate-800/50 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        2+ floors
                      </button>
                    </div>
                  </div>
                  {effectiveSqFt > 0 && (
                    <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-2.5 py-1.5">
                      <Layers size={14} className="text-slate-500" />
                      <span className="text-sm text-slate-300">
                        {effectiveSqFt.toLocaleString()} sq ft • {formData.floors} floor{formData.floors > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Property Address */}
              <div style={{ animation: 'fadeInUp 0.4s ease-out 0.2s both' }}>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                  <Building2 size={16} className="text-slate-500" />
                  Property Address
                </label>
                <input
                  type="text"
                  value={formData.propertyAddress}
                  onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
                  placeholder="123 Main Street, City, Postcode"
                  className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all"
                  required
                />
              </div>

              {/* Property Type & Turnaround in one row on larger screens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Property Type */}
                <div style={{ animation: 'fadeInUp 0.4s ease-out 0.25s both' }}>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    Property Type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, propertyType: 'residential' })}
                      className={`p-3 rounded-xl border transition-all ${
                        formData.propertyType === 'residential'
                          ? 'border-teal-500/50 bg-teal-500/10 shadow-lg shadow-teal-500/10'
                          : 'border-slate-700/50 bg-slate-900/30 hover:border-slate-600 hover:bg-slate-800/50'
                      }`}
                    >
                      <div className="text-sm font-medium text-white">Residential</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, propertyType: 'commercial' })}
                      className={`p-3 rounded-xl border transition-all ${
                        formData.propertyType === 'commercial'
                          ? 'border-teal-500/50 bg-teal-500/10 shadow-lg shadow-teal-500/10'
                          : 'border-slate-700/50 bg-slate-900/30 hover:border-slate-600 hover:bg-slate-800/50'
                      }`}
                    >
                      <div className="text-sm font-medium text-white">Commercial</div>
                    </button>
                  </div>
                </div>

                {/* Turnaround */}
                <div style={{ animation: 'fadeInUp 0.4s ease-out 0.3s both' }}>
                  <label className="text-sm font-medium text-slate-300 mb-2 block">
                    Turnaround
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, turnaround: 'standard' })}
                      className={`p-3 rounded-xl border transition-all ${
                        formData.turnaround === 'standard'
                          ? 'border-teal-500/50 bg-teal-500/10 shadow-lg shadow-teal-500/10'
                          : 'border-slate-700/50 bg-slate-900/30 hover:border-slate-600 hover:bg-slate-800/50'
                      }`}
                    >
                      <Clock size={16} className="mx-auto mb-1 text-slate-400" />
                      <div className="text-xs font-medium text-white">48hr</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, turnaround: 'express' })}
                      className={`p-3 rounded-xl border transition-all ${
                        formData.turnaround === 'express'
                          ? 'border-amber-500/50 bg-amber-500/10 shadow-lg shadow-amber-500/10'
                          : 'border-slate-700/50 bg-slate-900/30 hover:border-slate-600 hover:bg-slate-800/50'
                      }`}
                    >
                      <Zap size={16} className="mx-auto mb-1 text-amber-400" />
                      <div className="text-xs font-medium text-white">24hr</div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Branding Spec Card */}
              <div style={{ animation: 'fadeInUp 0.4s ease-out 0.32s both' }}>
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
                    <Palette size={16} className="text-purple-400" />
                    Brand Specification
                  </label>
                  {onOpenBranding && (
                    <button
                      type="button"
                      onClick={onOpenBranding}
                      className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      {brandingProfile?.agencyName ? 'Edit' : 'Set up branding →'}
                    </button>
                  )}
                </div>
                {brandingProfile?.agencyName || brandingProfile?.logoDataUrl ? (
                  <div className="rounded-xl border border-purple-800/40 bg-purple-900/10 p-3 flex items-center gap-3">
                    {brandingProfile.logoDataUrl && (
                      <img
                        src={brandingProfile.logoDataUrl}
                        alt="Brand logo"
                        className="h-10 w-auto max-w-[60px] object-contain rounded bg-white/5 p-1 shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      {brandingProfile.agencyName && (
                        <p
                          className="font-semibold text-white text-sm truncate"
                          style={{ fontFamily: brandingProfile.fontFamily }}
                        >
                          {brandingProfile.agencyName}
                        </p>
                      )}
                      {brandingProfile.tagline && (
                        <p className="text-xs text-slate-400 truncate">{brandingProfile.tagline}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1.5">
                        <div
                          className="w-4 h-4 rounded-full border border-white/10"
                          title={`Primary: ${brandingProfile.primaryColor}`}
                          style={{ background: brandingProfile.primaryColor }}
                        />
                        <div
                          className="w-4 h-4 rounded-full border border-white/10"
                          title={`Accent: ${brandingProfile.accentColor}`}
                          style={{ background: brandingProfile.accentColor }}
                        />
                        <span className="text-[10px] text-slate-500">{brandingProfile.fontFamily}</span>
                        {brandingProfile.watermarkEnabled && brandingProfile.logoDataUrl && (
                          <span className="text-[10px] text-purple-400 bg-purple-900/30 px-1.5 py-0.5 rounded">watermark</span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      <Sparkles size={14} className="text-purple-400" />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/30 p-3 flex items-center gap-3 text-slate-500">
                    <Palette size={16} />
                    <span className="text-sm">No branding set — floor plan will use standard styling.</span>
                    {onOpenBranding && (
                      <button
                        type="button"
                        onClick={onOpenBranding}
                        className="ml-auto text-xs text-purple-400 hover:text-purple-300 shrink-0 transition-colors"
                      >
                        Add branding →
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div style={{ animation: 'fadeInUp 0.4s ease-out 0.35s both' }}>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                  <FileText size={16} className="text-slate-500" />
                  Notes <span className="text-slate-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Room names, special requirements..."
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl bg-slate-900/50 border border-slate-700/50 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all resize-none"
                />
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-3" style={{ animation: 'fadeInUp 0.4s ease-out 0.4s both' }}>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                    <Mail size={14} className="text-slate-500" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    placeholder="you@email.com"
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-900/50 border border-slate-700/50 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-2">
                    <Phone size={14} className="text-slate-500" />
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="+44..."
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-900/50 border border-slate-700/50 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Pricing */}
              {formData.sqFt !== '' && (
                <div 
                  className={`rounded-2xl p-4 border backdrop-blur-sm transition-all ${
                    requiresCustomQuote
                      ? 'bg-gradient-to-r from-purple-900/20 to-violet-900/20 border-purple-500/30'
                      : hasEnoughCredits 
                        ? 'bg-gradient-to-r from-teal-900/20 to-cyan-900/20 border-teal-500/30'
                        : 'bg-gradient-to-r from-red-900/20 to-orange-900/20 border-red-500/30'
                  }`}
                  style={{ animation: 'fadeInUp 0.4s ease-out 0.45s both' }}
                >
                  {requiresCustomQuote ? (
                    // Bespoke quote required for properties over 10,000 sq ft
                    <div className="text-center py-2">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="p-2 rounded-lg bg-purple-500/20">
                          <MessageSquare size={18} className="text-purple-400" />
                        </div>
                        <div className="text-lg font-semibold text-purple-400">
                          Custom Quote Required
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm mb-3">
                        Properties over {CUSTOM_QUOTE_SQFT.toLocaleString()} sq ft require a bespoke quote.
                      </p>
                      <a
                        href="mailto:hello@sketchplan.studio?subject=Custom Quote Request&body=Property Details:%0A- Address: %0A- Square Footage: %0A- Property Type: %0A- Notes: "
                        className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm font-medium transition-colors"
                      >
                        <Mail size={14} />
                        Contact us for a quote
                        <ArrowRight size={14} />
                      </a>
                    </div>
                  ) : hasValidSqFt && isInvoiceClient ? (
                    // Monthly-invoice client — billed on a statement, no credits used
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-teal-500/20 shrink-0">
                        <PoundSterling size={18} className="text-teal-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs text-slate-400">Added to your monthly invoice</div>
                        <div className="text-2xl font-bold text-teal-400">
                          {effectivePricePence != null ? formatGBP(effectivePricePence) : '—'}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {hasCustomRate ? (
                            'Your agreed account rate'
                          ) : (
                            <>
                              {listPricePence != null && formatGBP(listPricePence)} · {planPrice.sizeBand.label}
                            </>
                          )}
                          {commercialUpliftPence > 0 && (
                            <span className="text-sky-400"> + {formatGBP(commercialUpliftPence)} commercial ({SKETCHPLAN_COMMERCIAL_RULE_SHORT})</span>
                          )}
                          {expressUpliftPence > 0 && (
                            <span className="text-amber-400"> + {formatGBP(expressUpliftPence)} express</span>
                          )}
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-300 bg-slate-800/50 rounded-lg px-2.5 py-1.5">
                          <FileText size={13} className="text-teal-400 shrink-0" />
                          No credits used — your account is billed monthly by invoice.
                        </div>
                      </div>
                    </div>
                  ) : hasValidSqFt ? (
                    // Volume + size pricing display (prepaid / credits)
                    <>
                      {/* How to pay: existing credit balance or a one-off card payment */}
                      <div className="mb-3 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setPayMethod('credits')}
                          className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border text-sm font-medium transition-all ${
                            payMethod === 'credits'
                              ? 'border-teal-500/50 bg-teal-500/10 text-teal-400'
                              : 'border-slate-700/50 bg-slate-900/30 text-slate-400 hover:border-slate-600'
                          }`}
                        >
                          <Coins size={16} />
                          Pay with credits
                        </button>
                        <button
                          type="button"
                          onClick={() => setPayMethod('card')}
                          className={`flex items-center justify-center gap-2 p-2.5 rounded-xl border text-sm font-medium transition-all ${
                            payingByCard
                              ? 'border-teal-500/50 bg-teal-500/10 text-teal-400'
                              : 'border-slate-700/50 bg-slate-900/30 text-slate-400 hover:border-slate-600'
                          }`}
                        >
                          <CreditCard size={16} />
                          Pay by card
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${canSettle ? 'bg-teal-500/20' : 'bg-red-500/20'}`}>
                            <PoundSterling size={18} className={canSettle ? 'text-teal-400' : 'text-red-400'} />
                          </div>
                          <div>
                            <div className="text-xs text-slate-400">Your price this plan</div>
                            <div className={`text-2xl font-bold ${canSettle ? 'text-teal-400' : 'text-red-400'}`}>
                              {effectivePricePence != null ? formatGBP(effectivePricePence) : '—'}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {hasCustomRate ? (
                                'Your agreed account rate'
                              ) : (
                                <>
                                  {listPricePence != null && formatGBP(listPricePence)} · {planPrice.sizeBand.label}
                                </>
                              )}
                              {commercialUpliftPence > 0 && (
                                <span className="text-sky-400"> + {formatGBP(commercialUpliftPence)} commercial ({SKETCHPLAN_COMMERCIAL_RULE_SHORT})</span>
                              )}
                              {expressUpliftPence > 0 && (
                                <span className="text-amber-400"> + {formatGBP(expressUpliftPence)} express</span>
                              )}
                              {payingByCard ? (
                                <span className="text-slate-500"> · paid securely by card</span>
                              ) : creditCost != null ? (
                                <span className="text-slate-500"> · paid with {creditCost} credits</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {!payingByCard && (
                          <div className="text-right">
                            <div className="text-xs text-slate-400">Your Balance</div>
                            <div className={`text-lg font-semibold ${hasEnoughCredits ? 'text-slate-300' : 'text-red-400'}`}>
                              {userCredits} <span className="text-xs font-normal text-slate-500">credits</span>
                            </div>
                          </div>
                        )}
                      </div>

                      <p className="mt-3 text-xs text-slate-500">{SKETCHPLAN_PRICING_COPY.amendment}</p>
                      {!payingByCard && !hasEnoughCredits && (
                        <div className="mt-3 space-y-3">
                          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 rounded-lg px-3 py-2">
                            <AlertCircle size={14} />
                            <span>You need {(creditCost || 0) - userCredits} more credits — or switch to “Pay by card”.</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              onTopUp(creditCost ?? undefined);
                            }}
                            className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 shadow-lg shadow-orange-900/30 hover:shadow-orange-900/50 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                          >
                            <Coins size={18} />
                            Top Up Credits
                            <ArrowRight size={18} />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-slate-400">Enter 100–50,000 sq ft to see pricing.</p>
                  )}
                </div>
              )}

              {error && (
                <div 
                  className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-400 text-sm flex items-center gap-2"
                  style={{ animation: 'fadeInUp 0.3s ease-out' }}
                >
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              {/* Turnaround Times Info */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                    <Clock size={20} className="text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1">Turnaround Times</h4>
                    <p className="text-slate-300 text-sm">
                      <span className="text-teal-400 font-medium">Standard:</span> 48 hours
                      <span className="mx-2">•</span>
                      <span className="text-amber-400 font-medium">Express:</span> 24 hours
                    </p>
                    <p className="text-slate-500 text-xs mt-2 flex items-center gap-1.5">
                      <AlertCircle size={12} className="text-amber-500" />
                      Business hours are Monday–Friday only. Weekends are not included.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting || isUploadingSketch || sketchStorageUrls.length === 0 || !hasValidSqFt || !canSettle || requiresCustomQuote}
                className={`w-full py-4 rounded-2xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                  requiresCustomQuote
                    ? 'bg-gradient-to-r from-purple-600 to-violet-600'
                    : canSettle
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 shadow-lg shadow-teal-900/30 hover:shadow-teal-900/50 hover:scale-[1.02] active:scale-[0.98]'
                      : 'bg-gradient-to-r from-slate-600 to-slate-700'
                }`}
                style={{ 
                  animation: canSettle && hasValidSqFt && !requiresCustomQuote ? 'pulse-glow 2s ease-in-out infinite' : 'none',
                }}
              >
                {isUploadingSketch ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Uploading {sketchUrls.length > 1 ? `Sketches (${uploadProgress}%)` : 'Sketch'}...
                  </>
                ) : isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Processing...
                  </>
                ) : !hasValidSqFt ? (
                  <>
                    <Ruler size={20} />
                    Enter square footage
                  </>
                ) : requiresCustomQuote ? (
                  <>
                    <MessageSquare size={20} />
                    Custom Quote Required
                  </>
                ) : isInvoiceClient ? (
                  <>
                    Place Order
                    <ArrowRight size={20} />
                  </>
                ) : payingByCard ? (
                  <>
                    <CreditCard size={20} />
                    Pay {effectivePricePence != null ? formatGBP(effectivePricePence) : ''} by card &amp; order
                  </>
                ) : !hasEnoughCredits ? (
                  <>
                    <Coins size={20} />
                    Need {(creditCost || 0) - userCredits} More Credits
                  </>
                ) : (
                  <>
                    Use {creditCost} Credits & Order
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              <p className="text-xs text-slate-500 text-center">
                {isUploadingSketch
                  ? `Uploading your ${sketchUrls.length > 1 ? 'sketches' : 'sketch'} for the team...`
                  : requiresCustomQuote
                    ? 'Contact us for a personalized quote for large properties'
                    : isInvoiceClient
                      ? 'Billed monthly by invoice • Delivered to your email'
                      : payingByCard
                        ? 'Secure card payment via Stripe • Delivered to your email'
                        : hasEnoughCredits
                          ? 'Credits deducted instantly • Delivered to your email'
                          : 'Top up credits or pay by card to place your order'}
              </p>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProOrderModal;
